import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { Box, AppBar, Toolbar } from "@mui/material";
import { Outlet, useLocation } from "react-router";
import RouteNavigationProgress from "./RouteNavigationProgress";
import { AnimatePresence } from "motion/react";
import { observer } from "mobx-react-lite";

import { NotificationInboxScopeSync } from "@/components/NotificationBell/NotificationBell";
import useStore from "@/hooks/useStore";
import { appBarStore } from "@/store/AppBarStore";
import { injectHoraizenHelpWidget, removeHoraizenHelpWidget, showHoraizenHelpWidget } from "@/utils/helpWidget";

import MobileNavOverlay from "./components/MobileNavOverlay";
import MobilePromptHeaderInline from "./components/MobilePromptHeaderInline";
import MobilePromptHeaderStacked from "./components/MobilePromptHeaderStacked";
import StandardAppBarToolbar from "./components/StandardAppBarToolbar";
import { APP_BAR_GLASS_MAP_TOOLBAR_SX, APP_BAR_LAVENDER_MAP_APP_BAR_SX, APP_BAR_LAVENDER_MAP_TOOLBAR_SX } from "./components/constants";
import InteractiveSidebar from "../Sidebar/InteractiveSidebar";

/**
 * Outermost layout flex container; Prompts Builder uses a fully transparent shell (no “ghost” sheet).
 * @param {object} props
 * @param {boolean} props.isPromptsBuilderRoute - `true` when `pathname === "/prompts"`.
 * @param {import("react").ReactNode} props.children
 * @returns {import("react").JSX.Element}
 */
function LayoutRootBox({ isPromptsBuilderRoute, children }) {
  if (isPromptsBuilderRoute) {
    return (
      <Box
        sx={{
          display: "flex",
          height: "100dvh",
          width: "100%",
          overflow: "hidden",
          bgcolor: "transparent",
          backgroundColor: "transparent",
          boxShadow: "none",
        }}
      >
        {children}
      </Box>
    );
  }
  return <Box sx={{ display: "flex", height: "100dvh", width: "100%", overflow: "hidden" }}>{children}</Box>;
}

/**
 * Fixed top AppBar aligned with the desktop sidebar width; hidden on desktop when chrome is suppressed.
 * @param {object} props
 * @param {boolean} props.hideDesktopAppBarChrome - When true, bar is `display:none` so it does not capture pointer events.
 * @param {number} props.desktopSidebarWidth - Sidebar width in px (collapsed vs expanded).
 * @param {boolean} props.mobileLavenderMapHeader - Opaque lavender shell (Parcel Search map, drawer closed).
 * @param {boolean} props.mobileGlassMapHeader - Frosted glass shell (Parcel Search map, drawer expanded).
 * @param {import("react").ReactNode} props.children - Typically the MUI `Toolbar` subtree.
 * @returns {import("react").JSX.Element}
 */
function AuthenticatedFixedAppBar({
  hideDesktopAppBarChrome,
  desktopSidebarWidth,
  mobileLavenderMapHeader,
  mobileGlassMapHeader,
  children,
}) {
  const baseSx = {
    ...(mobileLavenderMapHeader ? APP_BAR_LAVENDER_MAP_APP_BAR_SX : { backgroundColor: "transparent" }),
    left: { xs: 0, lg: `${desktopSidebarWidth}px` },
    width: { xs: "100%", lg: `calc(100% - ${desktopSidebarWidth}px)` },
    transition: "left 0.3s ease, width 0.3s ease",
    zIndex: mobileGlassMapHeader ? 26 : 20,
  };
  if (hideDesktopAppBarChrome) {
    return (
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{
          ...baseSx,
          // suppressFixedBar pages zero the spacer; when the bar stays `display:none`, avoid a fixed 64px
          // hit-target over the main column. Prompts Builder now keeps the bar visible (`suppressFixedBar: false`).
          display: "none",
          pointerEvents: "none",
        }}
      >
        {children}
      </AppBar>
    );
  }
  return (
    <AppBar position="fixed" color="transparent" elevation={0} sx={baseSx}>
      {children}
    </AppBar>
  );
}

/**
 * Toolbar wrapper measured for live height; optional Prompts mobile header styling (tinted row, stretch layout).
 * @param {object} props
 * @param {import("react").RefObject<HTMLElement | null>} props.appBarToolbarRef - Ref to the toolbar DOM for `ResizeObserver`.
 * @param {boolean} props.unifiedMobilePromptHeader - Mobile Prompts sheet header without hamburger.
 * @param {boolean} props.mobileGlassMapHeader - Mobile map overlay pages: dark frosted toolbar.
 * @param {boolean} props.mobileLavenderMapHeader - Mobile map pages: opaque lavender toolbar.
 * @param {number} props.appBarHeight - Default min height in px when not in unified mobile header mode.
 * @param {import("react").ReactNode} props.children
 * @returns {import("react").JSX.Element}
 */
function AuthenticatedAppToolbar({
  appBarToolbarRef,
  unifiedMobilePromptHeader,
  mobileGlassMapHeader,
  mobileLavenderMapHeader,
  appBarHeight,
  children,
}) {
  const baseSx = {
    minHeight: `${appBarHeight}px !important`,
    display: "flex",
    alignItems: "center",
    px: { xs: "10px", md: 2 },
  };
  if (unifiedMobilePromptHeader) {
    return (
      <Toolbar
        ref={appBarToolbarRef}
        disableGutters
        sx={{
          ...baseSx,
          bgcolor: "#FFF1EA",
          borderBottom: "1px solid rgba(215, 160, 130, 0.28)",
          minHeight: "auto !important",
          alignItems: "stretch",
          py: 1.25,
        }}
      >
        {children}
      </Toolbar>
    );
  }
  return (
    <Toolbar
      ref={appBarToolbarRef}
      disableGutters
      sx={{
        ...baseSx,
        ...(mobileGlassMapHeader ? APP_BAR_GLASS_MAP_TOOLBAR_SX : {}),
        ...(mobileLavenderMapHeader && !mobileGlassMapHeader ? APP_BAR_LAVENDER_MAP_TOOLBAR_SX : {}),
      }}
    >
      {children}
    </Toolbar>
  );
}

/**
 * Chooses mobile Prompts header (inline vs stacked) or the standard app bar toolbar.
 * @param {object} props
 * @param {boolean} props.unifiedMobilePromptHeader
 * @param {boolean} props.mobileTitleRowInlineActions - When true with unified header, render inline actions row.
 * @param {object | null | undefined} props.backAction - `{ onClick, icon? }` for back affordance.
 * @param {string | null | undefined} props.title
 * @param {unknown[]} props.rightComponents
 * @param {string[]} props.rightKeys - Stable keys aligned with `rightComponents`.
 * @param {unknown[]} props.middleComponents
 * @param {string[]} props.middleKeys
 * @param {unknown[]} props.legacyActions
 * @param {string[]} props.legacyKeys
 * @param {boolean} props.showMobileMenu
 * @param {boolean} props.isMobileMenuOpen
 * @param {() => void} props.onMobileMenuToggle
 * @param {boolean} props.glassMapChrome
 * @returns {import("react").JSX.Element}
 */
function AppBarToolbarBody({
  unifiedMobilePromptHeader,
  mobileTitleRowInlineActions,
  backAction,
  title,
  rightComponents,
  rightKeys,
  middleComponents,
  middleKeys,
  legacyActions,
  legacyKeys,
  showMobileMenu,
  isMobileMenuOpen,
  onMobileMenuToggle,
  glassMapChrome,
}) {
  if (unifiedMobilePromptHeader) {
    if (mobileTitleRowInlineActions) {
      return (
        <MobilePromptHeaderInline
          backAction={backAction}
          title={title}
          rightComponents={rightComponents}
          rightKeys={rightKeys}
        />
      );
    }
    return (
      <MobilePromptHeaderStacked
        backAction={backAction}
        title={title}
        rightComponents={rightComponents}
        rightKeys={rightKeys}
      />
    );
  }
  return (
    <StandardAppBarToolbar
      backAction={backAction}
      showMobileMenu={showMobileMenu}
      isMobileMenuOpen={isMobileMenuOpen}
      onMobileMenuToggle={onMobileMenuToggle}
      title={title}
      middleComponents={middleComponents}
      middleKeys={middleKeys}
      rightComponents={rightComponents}
      rightKeys={rightKeys}
      legacyActions={legacyActions}
      legacyKeys={legacyKeys}
      glassMapChrome={glassMapChrome}
    />
  );
}

/**
 * Main column beside the sidebar: flex column with optional transparent Prompts Builder chrome.
 * @param {object} props
 * @param {boolean} props.isPromptsBuilderRoute
 * @param {number} props.desktopSidebarWidth - Used for `marginLeft` on large breakpoints.
 * @param {import("react").ReactNode} props.children
 * @returns {import("react").JSX.Element}
 */
function MainColumnBox({ isPromptsBuilderRoute, desktopSidebarWidth, children }) {
  const baseSx = {
    flex: 1,
    height: "100dvh",
    minHeight: 0,
    marginLeft: { xs: 0, lg: `${desktopSidebarWidth}px` },
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    transition: "margin-left 0.3s ease",
  };
  if (isPromptsBuilderRoute) {
    return (
      <Box
        sx={{
          ...baseSx,
          bgcolor: "transparent",
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      >
        {children}
      </Box>
    );
  }
  return <Box sx={baseSx}>{children}</Box>;
}

/**
 * Scroll region under the fixed app bar; Prompts Builder locks vertical overflow on this wrapper.
 * @param {object} props
 * @param {boolean} props.isPromptsBuilderRoute
 * @param {number} props.effectiveAppBarHeight - Top offset matching measured toolbar (0 when desktop chrome hidden).
 * @param {import("react").ReactNode} props.children - Usually `<Outlet />`.
 * @returns {import("react").JSX.Element}
 */
function LayoutContentOutletBox({ isPromptsBuilderRoute, effectiveAppBarHeight, children }) {
  const baseSx = {
    flex: 1,
    minHeight: 0,
    overflowX: "hidden",
    marginTop: `${effectiveAppBarHeight}px`,
  };
  if (isPromptsBuilderRoute) {
    return (
      <Box
        className="authenticated-layout-content"
        sx={{
          ...baseSx,
          /* Prompts Builder: no outer page scroll — master card + columns manage height and internal scroll. */
          overflowY: "hidden",
          bgcolor: "transparent",
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {children}
      </Box>
    );
  }
  return (
    <Box className="authenticated-layout-content" sx={{ ...baseSx, overflowY: "auto" }}>
      {children}
    </Box>
  );
}

/**
 * AuthenticatedLayout - Layout component for authenticated routes
 *
 * AppBar + mobile nav pattern matches __old/experiment_header-bar (fixed bar, in-toolbar hamburger,
 * MobX-driven title/slots). Help widget injection remains auth-scoped.
 *
 * @param {Object} props
 * @param {Function} props.onCustomerChange - Callback when customer changes
 * @param {Function} [props.onActingUserChange] - Superadmin: set or clear acting user `{ userId, email, displayName }` or `null`
 * @param {Function} props.onSignOut - Callback for sign out action
 * @param {Function} props.onSidebarCollapseChange - Callback when sidebar collapse state changes
 * @param {boolean} props.isSidebarCollapsed - Whether the sidebar is currently collapsed
 * @returns {import("react").JSX.Element}
 */
const AuthenticatedLayout = observer(
  ({ onCustomerChange, onActingUserChange, onSignOut, onSidebarCollapseChange, isSidebarCollapsed }) => {
    const location = useLocation();
    /** Prompts Builder: no paper/chrome behind the dashboard gradient + master glass card (avoids “ghost” sheet). */
    const isPromptsBuilderRoute = location.pathname === "/prompts";
    const [isMobile, setIsMobile] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const appBarToolbarRef = useRef(null);
    const appBarHeight = 64;
    const [liveToolbarHeight, setLiveToolbarHeight] = useState(appBarHeight);
    const desktopSidebarWidth = isSidebarCollapsed ? 64 : 220;

    const { authStore } = useStore();

    const authenticatedOutletKey = `auth-outlet:${String(authStore.customer ?? "").trim()}:${String(authStore.userId ?? "").trim()}`;

    useEffect(() => {
      const ac = new AbortController();
      const signal = ac.signal;

      injectHoraizenHelpWidget(authStore?.cognitoUserId, authStore?.customer, { signal }).then(
        () => !signal.aborted && showHoraizenHelpWidget()
      );

      return () => {
        ac.abort();
        removeHoraizenHelpWidget();
      };
    }, [authStore?.cognitoUserId, authStore?.customer]);

    useEffect(() => {
      const mediaQuery = window.matchMedia("(max-width: 1199px)");
      const handleMediaChange = (e) => setIsMobile(e.matches);
      mediaQuery.addEventListener("change", handleMediaChange);
      setIsMobile(mediaQuery.matches);
      return () => mediaQuery.removeEventListener("change", handleMediaChange);
    }, []);

    // Close menu when route or mobile state changes (pathname triggers re-run on navigation)
    // biome-ignore lint/correctness/useExhaustiveDependencies: pathname intentionally in deps to close menu on nav
    useEffect(() => {
      if (isMobile) setIsMobileMenuOpen(false);
    }, [location.pathname, isMobile]);

    const handleMobileMenuToggle = useCallback(() => setIsMobileMenuOpen((prev) => !prev), []);
    const handleSidebarItemClick = useCallback(() => setIsMobileMenuOpen(false), []);
    const handleBackdropClick = useCallback(() => setIsMobileMenuOpen(false), []);

    const middleKeysRef = useRef({ keys: [] });
    const rightKeysRef = useRef({ keys: [] });
    const legacyKeysRef = useRef({ keys: [] });
    /**
     * Stable keys per slot index. Regenerate only when the slot **count** changes — not when the
     * parent passes a new array reference with the same length (otherwise MobX `setConfig` on
     * every re-render remounts slots and breaks stateful widgets e.g. Prompts version history).
     */
    const ensureKeys = (ref, arr, prefix) => {
      const list = arr ?? [];
      if (ref.current.keys.length !== list.length) {
        ref.current.keys = list.map((_, i) => `${prefix}-${i}`);
      }
      return ref.current.keys;
    };
    const middleKeys = ensureKeys(middleKeysRef, appBarStore.middleComponents, "middle-slot");
    const rightKeys = ensureKeys(rightKeysRef, appBarStore.rightComponents, "right-slot");
    const legacyKeys = ensureKeys(legacyKeysRef, appBarStore.actions, "legacy-action");

    const hideDesktopAppBarChrome = appBarStore.suppressFixedBar && !isMobile && !appBarStore.backAction;
    const effectiveAppBarHeight = hideDesktopAppBarChrome ? 0 : liveToolbarHeight;

    // biome-ignore lint/correctness/useExhaustiveDependencies: pathname intentionally in deps to close menu on nav
    useLayoutEffect(() => {
      if (hideDesktopAppBarChrome) return undefined;
      const el = appBarToolbarRef.current;
      if (!el) return undefined;
      const sync = () => {
        const h = el.getBoundingClientRect().height;
        setLiveToolbarHeight(h > 0 ? Math.ceil(h) : appBarHeight);
      };
      sync();
      const ro = new ResizeObserver(() => sync());
      ro.observe(el);
      return () => ro.disconnect();
    }, [
      hideDesktopAppBarChrome,
      location.pathname,
      isMobile,
      appBarStore.title,
      appBarStore.hideMobileMenuButton,
      appBarStore.rightComponents,
      appBarStore.middleComponents,
      appBarStore.backAction,
      appBarStore.mobileTitleRowInlineActions,
      appBarStore.mobileGlassMapHeader,
      appBarStore.mobileLavenderMapHeader,
    ]);

    /** Mobile: no hamburger — Prompts editor sheet (optional {@link appBarStore.backAction} + title; actions in {@link appBarStore.rightComponents}). */
    const unifiedMobilePromptHeader = isMobile && appBarStore.hideMobileMenuButton && Boolean(appBarStore.title);
    const mobileGlassMapHeader = isMobile && appBarStore.mobileGlassMapHeader;
    const mobileLavenderMapHeader = isMobile && appBarStore.mobileLavenderMapHeader;

    return (
      <LayoutRootBox isPromptsBuilderRoute={isPromptsBuilderRoute}>
        <RouteNavigationProgress />
        <NotificationInboxScopeSync />
        {/* Desktop Sidebar */}
        <Box sx={{ display: { xs: "none", lg: "block" } }}>
          <InteractiveSidebar
            isMobile={false}
            onCustomerChange={onCustomerChange}
            onActingUserChange={onActingUserChange}
            handleSignOut={onSignOut}
            onCollapseChange={onSidebarCollapseChange}
          />
        </Box>

{/* Top App Bar */}
<AuthenticatedFixedAppBar
  hideDesktopAppBarChrome={hideDesktopAppBarChrome}
  desktopSidebarWidth={desktopSidebarWidth}
  mobileLavenderMapHeader={mobileLavenderMapHeader}
  mobileGlassMapHeader={mobileGlassMapHeader}
>
  <AuthenticatedAppToolbar
    appBarToolbarRef={appBarToolbarRef}
    unifiedMobilePromptHeader={unifiedMobilePromptHeader}
    mobileGlassMapHeader={mobileGlassMapHeader}
    mobileLavenderMapHeader={mobileLavenderMapHeader}
    appBarHeight={appBarHeight}
  >
    <AppBarToolbarBody
      unifiedMobilePromptHeader={unifiedMobilePromptHeader}
      mobileTitleRowInlineActions={appBarStore.mobileTitleRowInlineActions}
      backAction={appBarStore.backAction}
      title={appBarStore.title}
      rightComponents={appBarStore.rightComponents}
      rightKeys={rightKeys}
      middleComponents={appBarStore.middleComponents}
      middleKeys={middleKeys}
      legacyActions={appBarStore.actions}
      legacyKeys={legacyKeys}
      showMobileMenu={isMobile && !appBarStore.hideMobileMenu && !appBarStore.hideMobileMenuButton}
      isMobileMenuOpen={isMobileMenuOpen}
      onMobileMenuToggle={handleMobileMenuToggle}
      glassMapChrome={mobileGlassMapHeader}
    />
  </AuthenticatedAppToolbar>
</AuthenticatedFixedAppBar>

        {/* Mobile Sidebar Overlay */}
        <AnimatePresence>
          {isMobile && isMobileMenuOpen && (
            <MobileNavOverlay
              onBackdropClick={handleBackdropClick}
              onSidebarItemClick={handleSidebarItemClick}
              onCustomerChange={onCustomerChange}
              onActingUserChange={onActingUserChange}
              onCollapseChange={onSidebarCollapseChange}
              onSignOut={onSignOut}
            />
          )}
        </AnimatePresence>

        {/* Main Content — column flex so the scroll region uses flex:1 + minHeight:0 (avoids sub-pixel overflow vs calc(100% - appBar)). */}
        <MainColumnBox isPromptsBuilderRoute={isPromptsBuilderRoute} desktopSidebarWidth={desktopSidebarWidth}>
          <LayoutContentOutletBox
            isPromptsBuilderRoute={isPromptsBuilderRoute}
            effectiveAppBarHeight={effectiveAppBarHeight}
          >
            <Outlet key={authenticatedOutletKey} />
          </LayoutContentOutletBox>
        </MainColumnBox>
      </LayoutRootBox>
    );
  }
);

// React.memo reduces re-renders when parent props are unchanged; observer() still subscribes to appBarStore.
export default React.memo(AuthenticatedLayout);
